import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, UseMutationResult, useQuery } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { TABLE, POSITION_PAGE_FILTERS } from '../consts';
import { POSITION_PARAMS } from '../consts/requestParamsKeys';
import {
  promoteMatchCandidate,
  deleteMatchCandidate,
  rejectMatchCandidates,
  updateMatches,
} from '../api/matchApi';
import PaginationResponseDto from '../interfaces/paginationResponse.interface';
import { FilterOptions } from '../interfaces/filterOptions.interface';
import { Sort } from '../interfaces/apiRequest.interface';
import {
  MatchStatusEnum, PositionStatusEnum, POSITION_STATE,
} from '../consts/enums';
import STRING_KEYS from '../language/keys';
import {
  createPosition, deletePosition, getPositions, getPositionById, updatePositionState,
} from '../api/positionApi';
import { cancelMatchFlow, runEngineMatchFlow } from '../api/matchFlowApi';
import {
  Position, PositionState, PositionCreateRequest, PositionCreateResponse, PositionDeleteRequest,
} from '../@types/position';
import useActionAutoComplete from './autoComplete/useActionAutoComplete';
import { ActiveFilters } from './useTablePageParams';

type UseGetPositions = {
  positions: Position[],
  totalCount?: number,
  loadPositions: boolean,
  refetch: () => void,
}

type UseGetPositionById = {
  position: Position | undefined,
  loadPosition: boolean,
  refetchPosition: () => void,
}

type UsePositionsGetFiltersType = {
  statusOptions: FilterOptions[],
  stateOptions: FilterOptions[],
  accountOptions: FilterOptions[],
  autoCompleteValue: string,
  onChangeAutoComplete: (value: string) => void,
  loadAccounts: boolean,
}

const LIMIT_GET_POSITIONS = TABLE.ROWS_PER_PAGE;

export const usePositionsGetFilters = (): UsePositionsGetFiltersType => {
  const { t: translate } = useTranslation();
  const [status] = useState<FilterOptions[]>([{
    label: translate(STRING_KEYS.POSITION_STATUSES[PositionStatusEnum.published]),
    value: PositionStatusEnum.published,
  },
  {
    label: translate(STRING_KEYS.POSITION_STATUSES[PositionStatusEnum.draft]),
    value: PositionStatusEnum.draft,
  }]);
  const [state] = useState<FilterOptions[]>([{
    label: translate(STRING_KEYS.POSITION_STATES[POSITION_STATE.OPEN]),
    value: POSITION_STATE.OPEN,
  },
  {
    label: translate(STRING_KEYS.POSITION_STATES[POSITION_STATE.CLOSED]),
    value: POSITION_STATE.CLOSED,
  }]);

  const {
    loadAccounts, accounts, onChangeAutoComplete, autoCompleteValue,
  } = useActionAutoComplete(['id', 'displayName']);

  const accountOptions = accounts?.map(((account) => ({
    label: account.displayName,
    value: account.id,
  })));

  return {
    statusOptions: status,
    stateOptions: state,
    accountOptions,
    onChangeAutoComplete,
    autoCompleteValue,
    loadAccounts,
  };
};

export const useGetPositionById = (id: string): UseGetPositionById => {
  const {
    data,
    isLoading,
    refetch,
  } = useQuery<Position>(`position_detail_${id}`, () => getPositionById(id));

  return {
    position: data,
    loadPosition: isLoading,
    refetchPosition: refetch,
  };
};

export const useGetPositions = (
  activeFilters: ActiveFilters,
  currentPage: number,
  activeSort: Sort,
): UseGetPositions => {
  const searchQuery = activeFilters[POSITION_PAGE_FILTERS.QUERY]?.[0];

  const {
    data,
    isFetching,
    refetch,
  } = useQuery<PaginationResponseDto<Position>>(
    ['positions', currentPage, activeFilters, activeSort],
    () => getPositions({
      page: currentPage,
      limit: LIMIT_GET_POSITIONS,
      contains: {
        [POSITION_PARAMS.JOB_TITLE]: searchQuery,
      },
      in: {
        [POSITION_PARAMS.STATUS]: activeFilters[POSITION_PAGE_FILTERS.STATUS],
        [POSITION_PARAMS.STATE]: activeFilters[POSITION_PAGE_FILTERS.STATE],
        [POSITION_PARAMS.ACCOUNT_ID]: activeFilters[POSITION_PAGE_FILTERS.ACCOUNTS],
      },
      order: activeSort,
    }), {
      keepPreviousData: true,
    });

  return {
    positions: data?.results || [],
    totalCount: data?.totalCount,
    loadPositions: isFetching,
    refetch,
  };
};

export const useCreatePosition = ():
  UseMutationResult<PositionCreateResponse, unknown, PositionCreateRequest, unknown> => {
  return useMutation((savedPosition) => {
    return createPosition(savedPosition);
  });
};

export const useDeletePosition = (onSuccessCallback?: () => void)
  :UseMutationResult<unknown, AxiosResponse, PositionDeleteRequest, unknown> => {
  return useMutation((params) => {
    return deletePosition(params);
  }, {
    onSuccess: () => {
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
  });
};

type UseUpdatePositionStateParams = {
  accountId: string,
  positionId: string,
  positionState: PositionState,
}

export const useUpdatePositionState = (onSuccessCallback?: () => void)
  :UseMutationResult<PositionState, AxiosError,
  UseUpdatePositionStateParams, unknown> => {
  return useMutation((updateObj) => {
    return updatePositionState(
      updateObj.accountId,
      updateObj.positionId,
      updateObj.positionState,
    );
  }, {
    onSuccess: onSuccessCallback,
  });
};

type EngineRequest = {
  accountId: string,
  positionId: string,
  matchFLowRunId?: string,
}

export const useEngineRunMatchFlow = (onSuccessCallback?: () => void)
:UseMutationResult<MatchFlowResponse, AxiosError, EngineRequest, unknown> => {
  return useMutation(({ accountId, positionId }: EngineRequest) => {
    return runEngineMatchFlow(accountId, positionId);
  }, {
    onSuccess: onSuccessCallback,
  });
};

export const useEngineRunCancelMatchFlow = (onSuccessCallback?: () => void)
  :UseMutationResult<string, AxiosError, EngineRequest, unknown> => {
  return useMutation(({ accountId, positionId, matchFLowRunId }: EngineRequest) => {
    return cancelMatchFlow(accountId, positionId, matchFLowRunId);
  }, {
    onSuccess: onSuccessCallback,
  });
};

type UsePromoteMatchCandidateParams = {
  accountId: string,
  positionId: string,
  ids: string[],
  status: MatchStatusEnum,
}

export const usePromoteMatchCandidate = (onSuccessCallback: () => void)
:UseMutationResult<MatchPromoteCandidateResponse, AxiosError,
UsePromoteMatchCandidateParams, unknown> => {
  return useMutation(({
    accountId, positionId, ids, status,
  }:UsePromoteMatchCandidateParams) => {
    return promoteMatchCandidate(accountId, positionId, ids, status);
  }, {
    onSuccess: () => onSuccessCallback(),
  });
};

type UseUpdateMatchParams = {
  accountId: string,
  positionId: string,
  ids: string[],
  status: MatchStatusEnum,
}

export const useUpdateMatches = (onSuccessCallback: () => void)
  :UseMutationResult<void, AxiosError,
  UseUpdateMatchParams, unknown> => {
  return useMutation(({
    accountId, positionId, ids, status,
  }:UseUpdateMatchParams) => {
    return updateMatches(accountId, positionId, ids, status);
  }, {
    onSuccess: () => onSuccessCallback(),
  });
};

type UseRejectMatchCandidatesParams = {
  accountId: string,
  positionId: string,
  ids: string[],
}

export const useRejectMatchCandidates = (onSuccessCallback: () => void)
:UseMutationResult<unknown, AxiosError,
UseRejectMatchCandidatesParams, unknown> => {
  return useMutation(({
    accountId, positionId, ids,
  }:UseRejectMatchCandidatesParams) => {
    return rejectMatchCandidates(accountId, positionId, ids);
  }, {
    onSuccess: () => onSuccessCallback(),
  });
};

type UseDeleteParams = {
  accountId: string,
  positionId: string,
  ids: string[],
}
export const useDeleteMatchCandidate = (onSuccessCallback: () => void)
:UseMutationResult<string, AxiosError, UseDeleteParams> => {
  return useMutation((match) => {
    return deleteMatchCandidate(
      match.accountId,
      match.positionId,
      match.ids,
    );
  }, {
    onSuccess: () => {
      onSuccessCallback();
    },
  });
};
